/**
 * Bespoke handler shapes that don't fit the three generic patterns
 * (distance / 3-pt angle / 4-pt angle). Each factory here targets a
 * specific tool geometry described in the legacy uChiroToolsWizard1.cpp.
 *
 * Naming: kebab-of-what-it-does, not of a specific tool. One factory
 * usually powers multiple tools (e.g. lineWithPerpDistance covers
 * McGregor, Chamberlain, and Klaus).
 */
import type { ChiroToolHandler, ChiroDrawable, ChiroPoint } from '../types';
import {
  distance, extendLine, midpoint, projectOntoLine, pointToLineDistance,
} from '../math';

const LINE_COLOR = '#ffd400';
const LABEL_STYLE = { color: LINE_COLOR, fontSize: 16 };
const DROP_STYLE = { color: LINE_COLOR, dashed: true };

/**
 * 2 clicks → draw a reference line, no measurement number. Used by
 * "MacRae's Line", "Boogard's Line", "Central Gravity Line", "Ullmann's
 * Line", "Intercrestal Line", and "Protrusio" — all tools where the
 * clinical use is visual assessment against the line, not a numeric
 * readout.
 */
export function referenceLineHandler(toolId: number, label: string): ChiroToolHandler {
  return {
    toolId,
    expectedPointCount: 2,
    render(points) {
      const out: ChiroDrawable[] = [];
      if (points.length >= 2) {
        const ext = extendLine(points[0], points[1], 1.0);
        out.push({ kind: 'line', a: ext.a, b: ext.b, style: { color: LINE_COLOR } });
        out.push({
          kind: 'label',
          at: midpoint(points[0], points[1]),
          text: label,
          style: { color: LINE_COLOR, fontSize: 12 },
        });
      }
      return out;
    },
    compute() {
      return {
        summary: `${label} drawn.`,
        rows: [{ label, value: 'reference line' }],
      };
    },
  };
}

/**
 * 3 clicks → line from clicks 1-2, perpendicular distance from click 3.
 * Used by McGregor's, Chamberlain's, and Klaus — all measure the
 * distance from an anatomical landmark (odontoid apex) to a reference
 * line joining two skull-base points.
 */
export function lineWithPerpDistanceHandler(toolId: number, label: string): ChiroToolHandler {
  return {
    toolId,
    expectedPointCount: 3,
    render(points) {
      const out: ChiroDrawable[] = [];
      if (points.length >= 2) {
        const ext = extendLine(points[0], points[1], 0.5);
        out.push({ kind: 'line', a: ext.a, b: ext.b, style: { color: LINE_COLOR } });
      }
      if (points.length >= 3) {
        const foot = projectOntoLine(points[2], points[0], points[1]);
        out.push({ kind: 'line', a: points[2], b: foot, style: DROP_STYLE });
        const d = pointToLineDistance(points[2], points[0], points[1]);
        out.push({
          kind: 'label',
          at: midpoint(points[2], foot),
          text: `${d.toFixed(1)} mm`,
          style: LABEL_STYLE,
        });
      }
      return out;
    },
    compute(points) {
      const d = pointToLineDistance(points[2], points[0], points[1]);
      return {
        summary: `${label}: ${d.toFixed(1)} mm`,
        rows: [{ label, value: `${d.toFixed(1)} mm` }],
      };
    },
  };
}

/**
 * 2 clicks → angle between the drawn line and image horizontal. Used by
 * Sacral Inclination and Lumbosacral Angle where the film-frame
 * horizontal is the clinical reference (patient supine).
 *
 * "Horizontal" here means world +X. On standard AP/lateral radiographs
 * that corresponds to screen horizontal. If the image is rotated on
 * display the result is still measured against the anatomical horizontal
 * (patient left-right), which is the clinically correct behaviour.
 */
export function angleFromHorizontalHandler(toolId: number, label: string): ChiroToolHandler {
  return {
    toolId,
    expectedPointCount: 2,
    render(points) {
      const out: ChiroDrawable[] = [];
      if (points.length >= 2) {
        const ext = extendLine(points[0], points[1], 0.3);
        out.push({ kind: 'line', a: ext.a, b: ext.b, style: { color: LINE_COLOR } });
        // Horizontal reference line drawn through the midpoint for visual
        // context. Length = 60% of the measurement line's length.
        const mid = midpoint(points[0], points[1]);
        const halfLen = distance(points[0], points[1]) * 0.3;
        const h1: ChiroPoint = { worldX: mid.worldX - halfLen, worldY: mid.worldY, worldZ: mid.worldZ };
        const h2: ChiroPoint = { worldX: mid.worldX + halfLen, worldY: mid.worldY, worldZ: mid.worldZ };
        out.push({ kind: 'line', a: h1, b: h2, style: { color: '#888', dashed: true } });
        const angle = angleFromHorizontal(points[0], points[1]);
        out.push({
          kind: 'label',
          at: mid,
          text: `${angle.toFixed(1)}°`,
          style: LABEL_STYLE,
        });
      }
      return out;
    },
    compute(points) {
      const angle = angleFromHorizontal(points[0], points[1]);
      return {
        summary: `${label}: ${angle.toFixed(1)}°`,
        rows: [{ label, value: `${angle.toFixed(1)}°` }],
      };
    },
  };
}

function angleFromHorizontal(a: ChiroPoint, b: ChiroPoint): number {
  const dx = b.worldX - a.worldX;
  const dy = b.worldY - a.worldY;
  let deg = Math.abs((Math.atan2(dy, dx) * 180) / Math.PI);
  if (deg > 90) deg = 180 - deg;
  return deg;
}

/**
 * N clicks — first click is a center landmark, subsequent clicks are
 * peripheral landmarks. Renders a spoke line from center to each
 * peripheral point and shows the distance in mm on each spoke.
 * Used by Vastine-Kinney Method (5 clicks: pineal centre + 4 radial
 * distances used to detect pineal shift).
 */
export function distancesFromCenterHandler(
  toolId: number,
  label: string,
  totalClicks: number,
): ChiroToolHandler {
  return {
    toolId,
    expectedPointCount: totalClicks,
    render(points) {
      const out: ChiroDrawable[] = [];
      if (points.length === 0) return out;
      const center = points[0];
      for (let i = 1; i < points.length; i++) {
        out.push({ kind: 'line', a: center, b: points[i], style: { color: LINE_COLOR } });
        const d = distance(center, points[i]);
        out.push({
          kind: 'label',
          at: midpoint(center, points[i]),
          text: `${d.toFixed(1)} mm`,
          style: LABEL_STYLE,
        });
      }
      return out;
    },
    compute(points) {
      const center = points[0];
      const rows = points.slice(1).map((p, i) => ({
        label: `Distance ${i + 1}`,
        value: `${distance(center, p).toFixed(1)} mm`,
      }));
      return {
        summary: `${label} — ${rows.map((r) => r.value).join(' / ')}`,
        rows,
      };
    },
  };
}
